<template>
  <div v-if="visible" class="comment-mask" @click.self="close">
    <div class="comment-dialog">
      <div class="dialog-head">
        <span class="dialog-title">评论 · {{ title || '内容' }}</span>
        <button class="close-btn" type="button" title="关闭" @click="close">
          <FaIcon :icon="faXmark" :size="13" />
        </button>
      </div>

      <div class="like-row">
        <button
          class="like-btn"
          :class="{ active: liked }"
          type="button"
          :title="liked ? '取消点赞' : '点赞'"
          @click="toggleLike"
        >
          <FaIcon :icon="liked ? faHeart : farHeart" :size="15" />
          <span class="like-label">{{ liked ? '已赞' : '点赞' }}</span>
          <span v-if="likeCount > 0" class="like-count">{{ likeCount }}</span>
        </button>
      </div>

      <div class="comment-list">
        <div v-if="loading" class="tip">加载中…</div>
        <div v-else-if="!comments.length" class="tip">还没有评论，来说点什么</div>
        <div v-for="c in comments" :key="c.id" class="comment-item">
          <UserAvatar class="comment-avatar" :url="c.avatarUrl" :name="c.nickname" />
          <div class="comment-body">
            <div class="comment-meta">
              <span class="comment-nick">{{ c.nickname || '匿名用户' }}</span>
              <span class="comment-time">{{ formatTime(c.createdAt) }}</span>
            </div>
            <div class="comment-text">{{ c.content }}</div>
          </div>
          <button
            v-if="c.canDelete"
            class="comment-del"
            type="button"
            title="删除这条评论"
            @click="removeComment(c)"
          >
            <FaIcon :icon="faTrashCan" :size="11" />
          </button>
        </div>
      </div>

      <div class="input-row">
        <textarea
          v-model="content"
          class="comment-input"
          maxlength="100"
          rows="2"
          placeholder="说点什么…（100 字以内）"
        ></textarea>
        <div class="input-foot">
          <span class="counter" :class="{ warn: content.length > 90 }">{{ content.length }}/100</span>
          <button class="send-btn" :disabled="!canSend" @click="submit">发送</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FaIcon from '@/components/common/FaIcon.vue'
import UserAvatar from '@/components/common/UserAvatar.vue'
import { faHeart } from '@fortawesome/free-solid-svg-icons/faHeart'
// regular 包里导出名仍是 faXxx，前缀体现在图标定义里，所以这里做别名
import { faHeart as farHeart } from '@fortawesome/free-regular-svg-icons/faHeart'
import { faXmark } from '@fortawesome/free-solid-svg-icons/faXmark'
import { faTrashCan } from '@fortawesome/free-solid-svg-icons/faTrashCan'
import { listComments, createComment, deleteComment, like, unlike, getLikeStatus } from '@/api/comment.js'
import { toNumber } from '@/utils/number.js'
import { notifySuccess, notifyError } from '@/utils/notify.js'

export default {
  name: 'CommentDialog',
  components: { FaIcon, UserAvatar },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    resourceType: {
      type: String,
      default: 'TIMEBLOCK'
    },
    resourceId: {
      type: [String, Number],
      default: null
    },
    title: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      comments: [],
      content: '',
      loading: false,
      liked: false,
      likeCount: 0,
      faHeart,
      farHeart,
      faXmark,
      faTrashCan
    }
  },
  computed: {
    canSend() {
      return this.content.trim().length > 0
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.content = ''
        this.load()
      }
    },
    resourceId() {
      if (this.visible) {
        this.load()
      }
    }
  },
  methods: {
    async load() {
      if (!this.resourceId) return
      this.loading = true
      try {
        const [commentRes, likeRes] = await Promise.all([
          listComments(this.resourceType, this.resourceId),
          getLikeStatus(this.resourceType, this.resourceId)
        ])
        this.comments = (commentRes.success && commentRes.data) ? commentRes.data : []
        if (likeRes.success && likeRes.data) {
          this.liked = Boolean(likeRes.data.liked)
          // 后端 Long 会被序列化成字符串（见 utils/number.js），必须转成数字
          this.likeCount = toNumber(likeRes.data.count)
        }
      } catch (e) {
        console.error('[CommentDialog] 加载评论失败', e)
      } finally {
        this.loading = false
      }
    },
    async submit() {
      const text = this.content.trim()
      if (!text) return
      try {
        const res = await createComment({
          resourceType: this.resourceType,
          resourceId: this.resourceId,
          content: text
        })
        if (!res.success || res.code !== 200) {
          notifyError('发送失败', res.message || '评论未能发送，请稍后重试')
          return
        }
        this.content = ''
        notifySuccess('评论已发送', '已保存到该时间块的评论区')
        await this.load()
        this.$emit('changed')
      } catch (e) {
        notifyError('发送失败', '网络异常，评论未能发送，请稍后重试')
      }
    },
    async removeComment(comment) {
      if (!window.confirm('确定删除这条评论吗？')) return
      try {
        const res = await deleteComment(comment.id)
        if (!res.success || res.code !== 200) {
          notifyError('删除失败', res.message || '这条评论未能删除，请稍后重试')
          return
        }
        notifySuccess('已删除', '评论已删除')
        await this.load()
        this.$emit('changed')
      } catch (e) {
        notifyError('删除失败', '网络异常，请稍后重试')
      }
    },
    async toggleLike() {
      const previous = this.liked
      const previousCount = this.likeCount
      // likeCount 可能是后端 Long 转来的字符串，先 toNumber 再算，避免 "0" + 1 拼成 "01"
      const current = toNumber(this.likeCount)
      this.liked = !this.liked
      this.likeCount = this.liked ? current + 1 : Math.max(current - 1, 0)
      try {
        const res = this.liked
          ? await like({ resourceType: this.resourceType, resourceId: this.resourceId })
          : await unlike(this.resourceType, this.resourceId)
        if (!res.success || res.code !== 200) {
          this.liked = previous
          this.likeCount = previousCount
          notifyError('操作失败', res.message || '点赞状态未能保存，请稍后重试')
          return
        }
        this.$emit('changed')
      } catch (e) {
        this.liked = previous
        this.likeCount = previousCount
        notifyError('操作失败', '网络异常，请稍后重试')
      }
    },
    formatTime(value) {
      if (!value) return ''
      const d = new Date(value)
      if (isNaN(d.getTime())) return ''
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      const h = String(d.getHours()).padStart(2, '0')
      const min = String(d.getMinutes()).padStart(2, '0')
      return `${m}-${day} ${h}:${min}`
    },
    close() {
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.comment-mask {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(38, 59, 41, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.comment-dialog {
  width: 420px;
  max-width: 92vw;
  max-height: 80vh;
  background: #fffdf2;
  border: 3px solid #57795c;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(38, 59, 41, 0.25);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.dialog-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 10px;
  border-bottom: 2px solid #c9d9b5;
}

.dialog-title {
  font-size: 15px;
  font-weight: 700;
  color: #263b29;
}

.close-btn {
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  border-radius: 7px;
  color: #57795c;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.close-btn:hover {
  background: #eef4e4;
  color: #35512d;
}

.like-row {
  padding: 10px 16px 0;
}

.like-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 2px solid #e3efe6;
  background: #f7fbf8;
  color: #7f8f76;
  font-weight: 700;
  font-size: 12px;
  padding: 5px 12px;
  border-radius: 12px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.like-btn:hover {
  border-color: #f0c9c6;
  color: #d9534f;
}

.like-btn.active {
  border-color: #f0c9c6;
  background: #fdeceb;
  color: #d9534f;
}

.like-count {
  font-size: 12px;
  line-height: 1;
}

.comment-list {
  flex: 1;
  min-height: 120px;
  max-height: 320px;
  overflow-y: auto;
  padding: 10px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.tip {
  color: #8a9a78;
  font-size: 13px;
  text-align: center;
  padding: 16px 0;
}

.comment-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.comment-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #b8d49b;
  border: 2px solid #57795c;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
}

.comment-body {
  flex: 1;
  min-width: 0;
}

.comment-meta {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.comment-nick {
  font-size: 13px;
  font-weight: 700;
  color: #263b29;
}

.comment-time {
  font-size: 11px;
  color: #8a9a78;
}

.comment-text {
  font-size: 13px;
  color: #3f5342;
  margin-top: 3px;
  word-break: break-word;
}

.comment-del {
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: #fde8e8;
  color: #c45c5c;
  border-radius: 8px;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s ease, color 0.15s ease;
}

.comment-del:hover {
  background: #c45c5c;
  color: #fff;
}

.input-row {
  border-top: 2px solid #c9d9b5;
  padding: 10px 16px 14px;
}

.comment-input {
  width: 100%;
  border: none;
  background: #f7f7df;
  border-radius: 12px;
  padding: 10px 12px;
  font-size: 13px;
  color: #263b29;
  outline: none;
  resize: none;
  font-family: inherit;
}

.comment-input:focus {
  box-shadow: 0 0 0 3px rgba(145, 166, 123, 0.35);
}

.input-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
}

.counter {
  font-size: 12px;
  color: #8a9a78;
}

.counter.warn {
  color: #c45c5c;
}

.send-btn {
  border: 2px solid #987a2c;
  background: #f1dc7b;
  color: #263b29;
  font-weight: 700;
  font-size: 13px;
  padding: 6px 18px;
  border-radius: 12px;
  cursor: pointer;
}

.send-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ===== 轻量扁平主题覆盖（参考稿配色）=====
   只改颜色 / 描边 / 圆角 / 阴影，尺寸与间距全部沿用上面的原值。 */
/* 弹窗外壳 */
.comment-mask{
  background:rgba(38,51,43,.35);
}
.comment-dialog{
  background:var(--lp-surface,#fff);
  border:1px solid var(--lp-border,#e3efe6);      /* 2026-10-04：去掉 3px 绿系描边，改极淡中性描边 */
  border-radius:1rem;
  box-shadow:var(--lp-shadow-lg,0 8px 24px rgba(52,102,68,.08));
}
.dialog-head{
  border-bottom:1px solid var(--lp-border-soft,#eef5f0);
}
.dialog-title{
  color:var(--lp-text,#26332b);
}
.close-btn{
  color:var(--lp-text-3,#94a59b);
}
.close-btn:hover{
  background:var(--lp-primary-soft,#e9f5ec);
  color:var(--lp-primary-dark,#2f7a4a);
}
/* 点赞按钮（红心为语义色，保持红） */
.like-btn{
  background:var(--lp-surface,#fff);
  border:2px solid var(--lp-border,#e3efe6);
  border-radius:0.75rem;
  color:var(--lp-text-2,#5f7268);
}
.tip{
  color:var(--lp-text-3,#94a59b);
}
/* 评论列表 */
.comment-avatar{
  background:var(--lp-primary,#4aa46a);
  border-color:var(--lp-primary-dark,#2f7a4a);
}
.comment-nick{
  color:var(--lp-text,#26332b);
}
.comment-time{
  color:var(--lp-text-3,#94a59b);
}
.comment-text{
  color:var(--lp-text,#26332b);
}
.comment-del{
  background:#fdecec;
  color:#d9534f;
}
.comment-del:hover{
  background:#d9534f;
  color:#fff;
}
/* 输入区 */
.input-row{
  border-top:1px solid var(--lp-border-soft,#eef5f0);
}
.comment-input{
  background:var(--lp-surface,#fff);
  border:2px solid var(--lp-border,#e3efe6);
  border-radius:0.5rem;
  color:var(--lp-text,#26332b);
}
.comment-input:focus{
  border-color:var(--lp-primary,#4aa46a);
  box-shadow:none;
}
.counter{
  color:var(--lp-text-3,#94a59b);
}
.counter.warn{
  color:#d9534f;
}
/* 主按钮（实心） */
.send-btn{
  background:var(--lp-primary,#4aa46a);
  border:2px solid var(--lp-primary,#4aa46a);
  border-radius:0.5rem;
  color:#fff;
}
.send-btn:hover:not(:disabled){
  background:var(--lp-primary-dark,#2f7a4a);
}
</style>
